import React, {useMemo} from "react"

import PropTypes from "prop-types"
import {HelmetProvider} from "react-helmet-async"
import injectSheet from "react-jss"
import {Provider} from "react-redux"
import {createBrowserRouter, Outlet, RouterProvider} from "react-router-dom"

import {Page} from "./constants/page/pages"
import {PagesArchitecture} from "./constants/page/pagesArchitecture"
import {AsyncTechnicalErrorScene} from "./layouts/asyncScenes"
import MainLayoutContainer from "./layouts/mainLayout"
import StyleLoaderLayout from "./layouts/styleLoaderLayout"
import {PageTitle} from "./shared-components/components"
import {MatomoTracker} from "./shared-components/components/MatomoTracker/matomoTracker"
import TextsProvider from "./shared-components/components/TextsProvider/textsProvider"
import HomePageEbu from "./shared-components/constants/homepageEbu"
import sheet from "./shared-components/styles/globalSheet"
import injectTexts from "./shared-components/styles/injectTexts"
import {getCookiesChoices} from "./store/selectors/localStorage/cookiesSelectors"
import {getActualPage} from "./store/selectors/temporaries/pageSelectors"
import texts from "./styles/globalText"
import NavigationInitializer from "./shared-components/services/navigate"

/**
 * Root of the application.
 * Set the provider to distribute the store in all components.
 * Set the router root.
 */
const technicalErrorPath = PagesArchitecture[Page.TECHNICAL_ERROR].route.replace(/^\//, "")

const Root = ({store, texts}) => {
    if (window.location.pathname === "/" || window.location.pathname === PagesArchitecture[Page.LOGIN].route) {
        window.location.replace(`${HomePageEbu.SAP}${PagesArchitecture[Page.LOGIN].route}`)
    }

    const router = useMemo(
        () =>
            createBrowserRouter(
                [
                    {
                        element: (
                            <>
                                <NavigationInitializer />
                                <PageTitle
                                    actualPageSelector={getActualPage}
                                    defaultTitle={texts.defaultPageTitle}
                                    pagesArchitecture={PagesArchitecture}
                                    suffix={texts.pageTitleSuffix}
                                />
                                <Outlet />
                            </>
                        ),
                        children: [
                            {
                                path: technicalErrorPath,
                                element: <AsyncTechnicalErrorScene />,
                            },
                            {
                                path: "*",
                                element: (
                                    <StyleLoaderLayout>
                                        <MainLayoutContainer />
                                    </StyleLoaderLayout>
                                ),
                            },
                        ],
                    },
                ],
                {basename: HomePageEbu.SAP},
            ),
        [texts],
    )

    return (
        <Provider store={store}>
            <HelmetProvider>
                <MatomoTracker getCookiesChoices={getCookiesChoices}>
                    <TextsProvider texts={texts}>
                        <RouterProvider router={router} />
                    </TextsProvider>
                </MatomoTracker>
            </HelmetProvider>
        </Provider>
    )
}

Root.propTypes = {
    store: PropTypes.object.isRequired,
    texts: PropTypes.object.isRequired,
}

export default injectTexts(texts, injectSheet(sheet, {inject: ["sheet"]})(Root))
